import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { Colors } from "helpers/Consts";
import { useTheme } from "@mui/system";
import { useFormik } from "formik";
import { IUserAddressAddParams } from "types/Address";
import { useMutationAddAddress } from "services/api/mutations/useMutationAddAddress";
import * as Yup from "yup";
import { useQueryAddresses } from "services/api/queries/useQueryAddresses";
import CepTextField from "ui/CepTextField";
import SearchIcon from "@mui/icons-material/Search";
import { useState } from "react";
import { EnderecoViaCep } from "types/EnderecoViaCep";
import { Snackbar } from "@material-ui/core";
import { ILoginData } from "../../types/Login";
import { UseQueryResult } from "react-query";
import { IShoppingBagData } from "../../types/ShoppingBag";

type Props = {
  open: boolean;
  handleClose: () => void;
  loginInfo: ILoginData;
  shoppingBagQuery: UseQueryResult<IShoppingBagData>
};

function CreateAddressDialog({ open, handleClose, loginInfo, shoppingBagQuery }: Props) {
  const theme = useTheme();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [snackBarErroCep, setSnackBarErroCep] = useState<boolean>(false);
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  //const loginInfo = useSelector((state: ICardapioAppState) => state?.loginInfo);
  const apiAddresses = useQueryAddresses(Number(loginInfo.id));
  const { mutateAsync: addAddressAsync } = useMutationAddAddress();
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      user_id: loginInfo.id,
      type: "H",
      complete_address: "",
      description: "",
      number: "",
      neighborhood: "",
      city: "",
      state: "",
      postal_code: "",
      lat: 0,
      lon: 0,
      active: false,
      additional_info: "",
      reference_point: "",
    } as IUserAddressAddParams,
    validationSchema: Yup.object().shape({
      description: Yup.string().required("Logradouro é obrigatório."),
      number: Yup.string()
        .min(1, "Número está incompleto")
        .required("Número é obrigatório."),
      neighborhood: Yup.string().required("Bairro é obrigatório."),
      city: Yup.string().required("Cidade é obrigatório."),
      state: Yup.string().required("Estado é obrigatório."),
      postal_code: Yup.string().required("CEP é obrigatório."),
    }),
    onSubmit: async (values) => {
      formik.setSubmitting(false);
      formik.values.complete_address = `${formik.values.description}, ${formik.values.number}, ${formik.values.neighborhood}, ${formik.values.city} - ${formik.values.state}`;
      // console.log("values ==>", values);

      const request = {...values, current_shopping_bag_id: shoppingBagQuery.data?.id ?? -1 }

      await addAddressAsync(request);
      apiAddresses.refetch();
      handleClose();
      setTimeout(() => {
        resetValues();
      }, 2000);
      // resetValues();
    },
  });

  function resetValues() {
    formik.values.user_id = loginInfo.id;
    formik.values.type = "H";
    formik.values.complete_address = "";
    formik.values.description = "";
    formik.values.number = "";
    formik.values.neighborhood = "";
    formik.values.city = "";
    formik.values.state = "";
    formik.values.postal_code = "";
    formik.values.lat = 0;
    formik.values.lon = 0;
    formik.values.active = false;
    formik.values.additional_info = "";
    formik.values.reference_point = "";
    setDisabled(true);
    setSnackBarErroCep(false);
  }

  const handleCEP = async () => {
    await fetch(`https://viacep.com.br/ws/${formik.values.postal_code}/json/`)
      .then((res) => res.json())
      .then((endereco: EnderecoViaCep) => {
        formik.values.neighborhood = endereco.bairro;
        formik.values.city = endereco.localidade;
        formik.values.additional_info = endereco.complemento;
        formik.values.state = endereco.uf;
        formik.values.description = endereco.logradouro;
      })
      .then(() => {
        if (formik.values.city !== "" && formik.values.city !== undefined) {
          setDisabled(false);
        } else {
          setDisabled(true);
          resetValues();
          setSnackBarErroCep(true);
        }
      });
  };
  const handleCloseSnackBar = () => {
    setSnackBarErroCep(!snackBarErroCep);
  };
  return (
    <>
      <Snackbar
        open={snackBarErroCep}
        autoHideDuration={2000}
        onClose={handleCloseSnackBar}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
      >
        <Alert
          onClose={handleCloseSnackBar}
          severity="error"
          sx={{ width: "100%" }}
        >
          Cep inválido.
        </Alert>
      </Snackbar>

      <Dialog
        fullScreen={fullScreen}
        open={open}
        onClose={() => {
          resetValues();
          handleClose();
        }}
        maxWidth={"lg"}
      >
        <DialogTitle color={Colors.orangePrimary}>
          <Box display="flex" alignItems="center">
            <Box>
              <IconButton
                color="primary"
                onClick={() => {
                  resetValues();
                  handleClose();
                }}
              >
                <ArrowBackIosIcon fill={Colors.orangePrimary} />
              </IconButton>
            </Box>
            <Box
              flexGrow={1}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Cadastrar novo Endereço
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: { md: "600px" },
              height: "100%",
              paddingX: "8px",
            }}
          >
            <form noValidate onSubmit={formik.handleSubmit}>
              <Box>
                <Box sx={{ display: "flex" }}>
                  <CepTextField
                    sx={{
                      width: "30%",
                    }}
                    id="postal_code"
                    label="CEP"
                    variant="standard"
                    type="text"
                    value={formik.values.postal_code}
                    onChange={formik.handleChange}
                    required
                  />
                  <IconButton size="large" onClick={handleCEP}>
                    <SearchIcon color="primary" />
                  </IconButton>
                </Box>
                <TextField
                  fullWidth
                  id="description"
                  label="Logradouro"
                  placeholder="ex: Rua Barbosa de Freitas"
                  variant="standard"
                  disabled={disabled}
                  type="text"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  required
                />
                <Box
                  display="flex"
                  sx={{
                    marginTop: "8px",
                  }}
                >
                  <TextField
                    sx={{
                      margin: "0 0 8px 0px",
                      width: "30%",
                    }}
                    id="number"
                    label="Numero"
                    placeholder="ex: 100"
                    variant="standard"
                    disabled={disabled}
                    type="text"
                    value={formik.values.number}
                    onChange={formik.handleChange}
                    required
                  />
                  <TextField
                    sx={{
                      marginX: "8px",
                      width: "70%",
                    }}
                    id="additional_info"
                    label="Complemento"
                    placeholder="ex: Bloco A"
                    variant="standard"
                    disabled={disabled}
                    type="text"
                    value={formik.values.additional_info}
                    onChange={formik.handleChange}
                  />
                </Box>
                <TextField
                  fullWidth
                  id="neighborhood"
                  label="Bairro"
                  placeholder="Bairro"
                  variant="standard"
                  disabled={disabled}
                  type="text"
                  value={formik.values.neighborhood}
                  onChange={formik.handleChange}
                  required
                />
                <Box
                  display="flex"
                  sx={{
                    marginTop: "8px",
                  }}
                >
                  <TextField
                    sx={{
                      margin: "0 0 8px 0px",
                      width: "70%",
                    }}
                    id="city"
                    label="Cidade"
                    placeholder="Cidade"
                    variant="standard"
                    disabled={disabled}
                    type="text"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    required
                  />
                  <TextField
                    sx={{
                      marginX: "8px",
                      width: "30%",
                    }}
                    id="state"
                    label="Estado/UF"
                    placeholder="UF"
                    variant="standard"
                    disabled={disabled}
                    type="text"
                    value={formik.values.state}
                    onChange={formik.handleChange}
                    required
                  />
                </Box>
                <Box display="flex">
                  <TextField
                    sx={{
                      margin: "0 0 8px 0px",
                      width: "70%",
                    }}
                    id="reference_point"
                    label="Ponto de Referência"
                    placeholder="Ponto de Referência"
                    variant="standard"
                    disabled={disabled}
                    type="text"
                    value={formik.values.reference_point}
                    onChange={formik.handleChange}
                  />
                </Box>
              </Box>
              {/* {(formik.errors && formik.dirty) && (
            <Alert
              severity="warning"
              sx={{
                display: "flex",
                flexGrow: 1,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography fontSize="14px" fontWeight={700}>
                Erro no cadastro
              </Typography>
            </Alert>
          )} */}
              <Box
                sx={{
                  width: "100%",
                }}
              >
                <Button
                  sx={{
                    width: "100%",
                    color: "white",
                    textTransform: "none",
                  }}
                  type="submit"
                  variant="contained"
                  disabled={
                    !(formik.isValid && formik.dirty) ||
                    formik.values.city === undefined ||
                    formik.values.city === ""
                  }
                >
                  Adicionar Endereço
                </Button>
              </Box>
            </form>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CreateAddressDialog;
